import type { SiteThemeId } from "../../themes";

/**
 * Backgrounds for sections, each drawn in siteBackgrounds.css (loaded with
 * blockFlow.css, so this module stays importable in tests) as
 * `site-background-<id>`. `tone` says whether text on it should be dark or
 * light; theme-relative ones (None, Soft, Contrast) follow the theme.
 */
export const SITE_BACKGROUNDS = {
  none: { label: "None", tone: null },
  tint: { label: "Soft", tone: null },
  contrast: { label: "Contrast", tone: null },
  white: { label: "White", tone: "light" },
  mist: { label: "Mist", tone: "light" },
  stone: { label: "Stone", tone: "light" },
  sand: { label: "Sand", tone: "light" },
  blush: { label: "Blush", tone: "light" },
  "gold-pale": { label: "Pale gold", tone: "light" },
  "sage-pale": { label: "Pale sage", tone: "light" },
  terracotta: { label: "Terracotta", tone: "dark" },
  clay: { label: "Clay", tone: "dark" },
  forest: { label: "Forest", tone: "dark" },
  ink: { label: "Ink", tone: "dark" },
  deep: { label: "Deep", tone: "dark" },
  sunrise: { label: "Sunrise", tone: "light" },
  meadow: { label: "Meadow", tone: "light" },
  rose: { label: "Rose", tone: "light" },
  fog: { label: "Fog", tone: "light" },
  "golden-hour": { label: "Golden hour", tone: "light" },
  dusk: { label: "Dusk", tone: "dark" },
  aurora: { label: "Aurora", tone: "dark" },
  charcoal: { label: "Charcoal", tone: "dark" },
} as const;
export type SiteBackground = keyof typeof SITE_BACKGROUNDS;
export const SITE_BACKGROUND_IDS = Object.keys(SITE_BACKGROUNDS) as [
  SiteBackground,
  ...SiteBackground[],
];

/**
 * The backgrounds offered with each theme: theme-relative ones, then colours
 * that pair with it, then gradients. Any background still renders under any
 * theme, so changing theme never loses a section's choice.
 */
export const THEME_BACKGROUNDS: Record<SiteThemeId, SiteBackground[]> = {
  warm: ["none", "tint", "contrast", "white", "blush", "gold-pale", "sage-pale", "sand", "terracotta", "forest", "sunrise", "meadow", "dusk"],
  gallery: ["none", "tint", "contrast", "mist", "stone", "blush", "sage-pale", "ink", "terracotta", "fog", "rose", "charcoal"],
  night: ["none", "tint", "contrast", "deep", "forest", "clay", "gold-pale", "dusk", "aurora", "golden-hour"],
};

/** Sections saved before backgrounds had a style of Plain, Tinted or Contrast. */
const legacy: Record<string, SiteBackground> = { plain: "none", tinted: "tint", contrast: "contrast" };

/** A section's background, reading the older style for sections saved before. */
export const sectionBackground = (content: unknown): SiteBackground => {
  const { background, style } = (content ?? {}) as { background?: string; style?: string };
  if (background && background in SITE_BACKGROUNDS) return background as SiteBackground;
  return legacy[style ?? ""] ?? "none";
};

/** The attributes that paint a section's background and set its text tone. */
export const sectionBackgroundAttributes = (background: SiteBackground) => ({
  class: `site-section site-background-${background}`,
  "data-background": background,
  ...(SITE_BACKGROUNDS[background].tone ? { "data-tone": SITE_BACKGROUNDS[background].tone } : {}),
});
